import { defineMessage, defineMessages } from "react-intl";

/** Error codes of the Explorer API mapped to readable text (the API never translates). */
/** `conflict` with a stable reason the API sends in `details.reason` (spec explorer-access, platform-administration). */
export const conflictReasonMessages = defineMessages({
  console_user_exists: {
    id: "access.error.conflict.consoleUserExists",
    defaultMessage:
      "This email already has a console account. A person belongs to one organization only, and a Platform Administrator to none: deactivate the current account first, or use another email.",
  },
  operator_already_linked: {
    id: "access.error.conflict.operatorAlreadyLinked",
    defaultMessage: "This operator is already linked to an organization.",
  },
  operator_not_linked: {
    id: "access.error.conflict.operatorNotLinked",
    defaultMessage: "The operator is no longer linked to this organization. Reload the page.",
  },
});

export const errorMessages = defineMessages({
  invalid_credentials: {
    id: "access.error.invalidCredentials",
    defaultMessage: "The email or password is not correct.",
  },
  unauthenticated: {
    id: "access.error.unauthenticated",
    defaultMessage: "Your session has ended. Sign in again to continue.",
  },
  sso_failed: {
    id: "access.error.ssoFailed",
    defaultMessage:
      "We could not complete the single sign-on. Try again or ask your Administrator.",
  },
  sso_required: {
    id: "access.error.ssoRequired",
    defaultMessage:
      "Your organization signs in through its identity provider. Use the single sign-on button.",
  },
  invalid_mfa_code: {
    id: "access.error.invalidMfaCode",
    defaultMessage: "The code is not correct. Enter the current code from your authenticator app.",
  },
  mfa_challenge_expired: {
    id: "access.error.mfaChallengeExpired",
    defaultMessage: "The code step has expired. Enter your email and password again.",
  },
  mfa_reset_pending: {
    id: "access.error.mfaResetPending",
    defaultMessage:
      "Your two-factor authentication was reset. Use the recovery link from your Administrator.",
  },
  two_factor_required: {
    id: "access.error.twoFactorRequired",
    defaultMessage:
      "Two-factor authentication is mandatory for your account. You can replace the authenticator instead.",
  },
  sso_not_available: {
    id: "access.error.ssoNotAvailable",
    defaultMessage: "Single sign-on is not available for this account.",
  },
  forbidden: {
    id: "access.error.forbidden",
    defaultMessage: "You do not have permission to do this.",
  },
  csrf_token_invalid: {
    id: "access.error.csrf",
    defaultMessage: "Your session is out of date. Reload the page and try again.",
  },
  not_found: {
    id: "access.error.notFound",
    defaultMessage: "Not found. It may have been removed or you may not have access to it.",
  },
  already_blocked: {
    id: "access.error.alreadyBlocked",
    defaultMessage: "This player or device already has an active block.",
  },
  block_not_active: {
    id: "access.error.blockNotActive",
    defaultMessage: "This block is no longer active. Reload to see its current status.",
  },
  export_not_ready: {
    id: "access.error.exportNotReady",
    defaultMessage: "This export is not ready yet. You will get a notification when it is.",
  },
  export_limit_reached: {
    id: "access.error.exportLimitReached",
    defaultMessage:
      "You already have 3 exports being generated. Wait for one to finish before requesting another.",
  },
  export_expired: {
    id: "access.error.exportExpired",
    defaultMessage:
      "This export file is no longer available (files are kept 7 days). Export the list again.",
  },
  credential_limit_reached: {
    id: "access.error.credentialLimitReached",
    defaultMessage:
      "This operator already has 2 active API credentials in this installation. Revoke one before creating another.",
  },
  conflict: {
    id: "access.error.conflict",
    defaultMessage: "This conflicts with the current state. Reload and try again.",
  },
  invitation_used: {
    id: "access.error.invitationUsed",
    defaultMessage: "This invitation was already used. Sign in, or ask for a new invitation.",
  },
  invitation_expired: {
    id: "access.error.invitationExpired",
    defaultMessage:
      "This invitation has expired. Ask your Administrator or a Platform Administrator for a new one.",
  },
  invitation_revoked: {
    id: "access.error.invitationRevoked",
    defaultMessage: "This invitation was revoked. Ask your Administrator for a new one.",
  },
  own_account_protected: {
    id: "access.error.ownAccountProtected",
    defaultMessage:
      "You cannot deactivate your own account, remove your own access or lower your own role. Ask another Administrator.",
  },
  last_administrator: {
    id: "access.error.lastAdministrator",
    defaultMessage:
      "This is the last active Administrator of the operator and cannot be deactivated.",
  },
  operator_deactivated: {
    id: "access.error.operatorDeactivated",
    defaultMessage:
      "This operator is deactivated: its configuration is read-only and nothing was changed.",
  },
  last_platform_administrator: {
    id: "access.error.lastPlatformAdministrator",
    defaultMessage:
      "You cannot deactivate yourself, and the last active Platform Administrator cannot be deactivated.",
  },
  identity_provider_not_tested: {
    id: "access.error.identityProviderNotTested",
    defaultMessage: "Run a successful test after the latest change before activating.",
  },
  rate_limited: {
    id: "access.error.rateLimited",
    defaultMessage: "Too many attempts. Wait a moment and try again.",
  },
  // the Fleet tab's step-up cap (add-control-plane-console, review cycle 2 of #282)
  step_up_rate_limited: {
    id: "access.error.stepUpRateLimited",
    defaultMessage: "Too many confirmations in a short time. Wait a few minutes and try again.",
  },
  server_error: {
    id: "access.error.serverError",
    defaultMessage: "The service is unavailable right now. Nothing was changed; try again shortly.",
  },
  network_error: {
    id: "access.error.network",
    defaultMessage: "We could not reach the server. Check your connection and try again.",
  },
  invalid_reset_code: {
    id: "access.error.invalidResetCode",
    defaultMessage:
      "This code is not valid: it may be wrong, expired (30 minutes), already used or replaced by a newer one. Check the latest email or ask for a new code.",
  },
  invalid_request: {
    id: "access.error.invalidRequest",
    defaultMessage: "Some fields are missing or invalid. Check them and try again.",
  },
  unknown: {
    id: "access.error.unknown",
    defaultMessage: "Something went wrong. Try again.",
  },
});

/** A bare `403` off the contract: the edge firewall (CloudFront WAF) refused the request. */
export const edgeRefusedMessage = defineMessage({
  id: "access.error.edgeRefused",
  defaultMessage:
    "The platform's security filter refused this request; nothing was changed. If it persists, tell the S-Trust team what you were doing.",
});

export const accountLockedMessage = defineMessage<{ minutes: number }>(
  {
    id: "access.error.accountLocked",
    defaultMessage:
      "Too many failed attempts. The account is locked; try again in {minutes, plural, one {# minute} other {# minutes}} or ask an Administrator to unlock it.",
  },
  { typed: true },
);

export const weakPasswordMessages = defineMessages({
  common_password: {
    id: "access.error.weakPassword.common",
    defaultMessage: "This password is too common. Choose another one.",
  },
  compromised_password: {
    id: "access.error.weakPassword.compromised",
    defaultMessage: "This password appears in known data breaches. Choose another one.",
  },
  fallback: {
    id: "access.error.weakPassword.fallback",
    defaultMessage: "This password does not meet the password policy.",
  },
});

export const passwordRevealMessages = defineMessages({
  show: { id: "access.password.show", defaultMessage: "Show password" },
  hide: { id: "access.password.hide", defaultMessage: "Hide password" },
});

export const loginMessages = defineMessages({
  title: { id: "access.login.title", defaultMessage: "Sign in to S-Trust Explorer" },
  email: { id: "access.login.email", defaultMessage: "Email" },
  password: { id: "access.login.password", defaultMessage: "Password" },
  submit: { id: "access.login.submit", defaultMessage: "Sign in" },
  sso: { id: "access.login.sso", defaultMessage: "Continue with single sign-on (SSO)" },
  ssoHint: {
    id: "access.login.ssoHint",
    defaultMessage: "Enter your work email, then use single sign-on if your organization has it.",
  },
  emailRequired: { id: "access.login.emailRequired", defaultMessage: "Enter your email." },
  passwordRequired: { id: "access.login.passwordRequired", defaultMessage: "Enter your password." },
  sessionEnded: {
    id: "access.login.sessionEnded",
    defaultMessage: "Your session has ended. Sign in again to continue.",
  },
  signedOut: { id: "access.login.signedOut", defaultMessage: "You have signed out." },
  auditNote: {
    id: "access.login.auditNote",
    defaultMessage: "Access is by invitation only. Every sign-in is audited.",
  },
  or: { id: "access.login.or", defaultMessage: "or" },
  redirecting: {
    id: "access.login.redirecting",
    defaultMessage: "Taking you to your organization's identity provider",
  },
  forgotPassword: { id: "access.login.forgotPassword", defaultMessage: "Forgot password?" },
  passwordReset: {
    id: "access.login.passwordReset",
    defaultMessage: "Your password was changed. Sign in with the new password.",
  },
  // a 503 with details.reason password_changed (verify-console-account-settings design D2)
  passwordResetUnfinished: {
    id: "access.login.passwordResetUnfinished",
    defaultMessage:
      "Your new password is set, but the reset could not be finished just now. Sign in with the new password; if the service is still unavailable, try again in a few minutes.",
  },
});

/** Self-service password reset (`/forgot-password`, change add-console-account-settings D14). */
export const forgotPasswordMessages = defineMessages({
  title: { id: "access.forgotPassword.title", defaultMessage: "Reset your password" },
  requestSubtitle: {
    id: "access.forgotPassword.requestSubtitle",
    defaultMessage: "Enter the email you sign in with. We will email you a 6-digit code.",
  },
  senderHint: {
    id: "access.forgotPassword.senderHint",
    defaultMessage:
      "The code comes from no-reply@verificationemail.com and may land in your spam folder. It is valid for 30 minutes and works once.",
  },
  ssoHint: {
    id: "access.forgotPassword.ssoHint",
    defaultMessage:
      "If your organization uses single sign-on, you sign in through your organization and get no code: change your password there.",
  },
  sendCode: { id: "access.forgotPassword.sendCode", defaultMessage: "Send code" },
  sent: {
    id: "access.forgotPassword.sent",
    defaultMessage:
      "If an account that signs in with a password uses this email, a 6-digit code is on its way. Check your inbox and spam folder.",
  },
  confirmSubtitle: {
    id: "access.forgotPassword.confirmSubtitle",
    defaultMessage: "Enter the code from the email and choose a new password.",
  },
  code: { id: "access.forgotPassword.code", defaultMessage: "6-digit code" },
  codeHint: {
    id: "access.forgotPassword.codeHint",
    defaultMessage: "Only the latest code works.",
  },
  codeInvalid: {
    id: "access.forgotPassword.codeInvalid",
    defaultMessage: "Enter the 6 digits of the code.",
  },
  submit: { id: "access.forgotPassword.submit", defaultMessage: "Set new password" },
  newCode: { id: "access.forgotPassword.newCode", defaultMessage: "Ask for a new code" },
  newCodeSent: {
    id: "access.forgotPassword.newCodeSent",
    defaultMessage:
      "A new code was asked for. Earlier codes stop working; at most 3 codes are sent per hour.",
  },
  otherEmail: { id: "access.forgotPassword.otherEmail", defaultMessage: "Use another email" },
  backToLogin: { id: "access.forgotPassword.backToLogin", defaultMessage: "Back to sign in" },
  twoFactorNote: {
    id: "access.forgotPassword.twoFactorNote",
    defaultMessage:
      "Nobody is signed in by a reset: every open session ends, and you sign in again, with your authenticator code if you use one.",
  },
  administratorNote: {
    id: "access.forgotPassword.administratorNote",
    defaultMessage: "No code? Your Administrator can still send you a password-setup invitation.",
  },
});

export const forgotPasswordEmailMessage = defineMessage<{ email: string }>(
  { id: "access.forgotPassword.email", defaultMessage: "Code sent for {email}" },
  { typed: true },
);

export const invitationMessages = defineMessages({
  title: { id: "access.invitation.title", defaultMessage: "Accept your invitation" },
  loading: { id: "access.invitation.loading", defaultMessage: "Checking your invitation" },
  kindAccount: {
    id: "access.invitation.kind.account",
    defaultMessage: "You were invited to join S-Trust Explorer.",
  },
  kindPasswordSetup: {
    id: "access.invitation.kind.passwordSetup",
    defaultMessage: "Set a new password for your S-Trust Explorer account.",
  },
  kindPlatformAdministrator: {
    id: "access.invitation.kind.platformAdministrator",
    defaultMessage: "You were invited to administer the S-Trust platform.",
  },
  kindTwoFactorRecovery: {
    id: "access.invitation.kind.twoFactorRecovery",
    defaultMessage: "Set a new password and a new authenticator for your S-Trust Explorer account.",
  },
  password: { id: "access.invitation.password", defaultMessage: "New password" },
  confirm: { id: "access.invitation.confirm", defaultMessage: "Repeat the password" },
  passwordRule: {
    id: "access.invitation.passwordRule",
    defaultMessage:
      "At least 12 characters. Common passwords and passwords found in data breaches are refused.",
  },
  tooShort: {
    id: "access.invitation.tooShort",
    defaultMessage: "The password must have at least 12 characters.",
  },
  mismatch: {
    id: "access.invitation.mismatch",
    defaultMessage: "The two passwords are not the same.",
  },
  submit: { id: "access.invitation.submit", defaultMessage: "Set password and sign in" },
  ssoSubmit: { id: "access.invitation.ssoSubmit", defaultMessage: "Continue with single sign-on" },
  ssoHint: {
    id: "access.invitation.ssoHint",
    defaultMessage:
      "Your organization signs in through its identity provider; no password is needed.",
  },
  retry: { id: "access.invitation.retry", defaultMessage: "Try again" },
  goToLogin: { id: "access.invitation.goToLogin", defaultMessage: "Go to sign in" },
  rulesLabel: { id: "access.invitation.rules.label", defaultMessage: "Password rules" },
  ruleLength: { id: "access.invitation.rules.length", defaultMessage: "12+ characters" },
  ruleMatch: { id: "access.invitation.rules.match", defaultMessage: "Passwords match" },
  ruleCommon: { id: "access.invitation.rules.common", defaultMessage: "Not a common password" },
  ruleFailed: { id: "access.invitation.rules.failed", defaultMessage: "Refused" },
  ruleMet: { id: "access.invitation.rules.met", defaultMessage: "Met" },
  ruleUnmet: { id: "access.invitation.rules.unmet", defaultMessage: "Not met yet" },
  singleUse: {
    id: "access.invitation.singleUse",
    defaultMessage: "Single-use link. It stops working once accepted, revoked or after 72 h.",
  },
});

export const invitationValidMessage = defineMessage<{ hours: number }>(
  { id: "access.invitation.validHours", defaultMessage: "Invitation · valid {hours} h" },
  { typed: true },
);

export const userMenuMessages = defineMessages({
  settings: { id: "access.userMenu.settings", defaultMessage: "Settings" },
  signOut: { id: "access.userMenu.signOut", defaultMessage: "Sign out" },
});

/** The steps after a right password (change add-console-account-settings, design D5). */
export const loginStepMessages = defineMessages({
  codeTitle: { id: "access.loginStep.codeTitle", defaultMessage: "Enter your code" },
  codeSubtitle: {
    id: "access.loginStep.codeSubtitle",
    defaultMessage: "Open your authenticator app and enter the code it shows for S-Trust Explorer.",
  },
  verify: { id: "access.loginStep.verify", defaultMessage: "Verify" },
  restart: { id: "access.loginStep.restart", defaultMessage: "Back to sign in" },
  setupTitle: {
    id: "access.loginStep.setupTitle",
    defaultMessage: "Set up two-factor authentication",
  },
  setupSubtitle: {
    id: "access.loginStep.setupSubtitle",
    defaultMessage:
      "Your account needs an authenticator app before you can continue. This takes about a minute.",
  },
  recoverySubtitle: {
    id: "access.loginStep.recoverySubtitle",
    defaultMessage:
      "Your password is set. Now add a new authenticator app to finish recovering your account.",
  },
  expired: {
    id: "access.loginStep.expired",
    defaultMessage: "This step has expired. Enter your email and password again.",
  },
  newPasswordTitle: {
    id: "access.loginStep.newPasswordTitle",
    defaultMessage: "Choose your password",
  },
  newPasswordSubtitle: {
    id: "access.loginStep.newPasswordSubtitle",
    defaultMessage:
      "You signed in with the temporary password from your email. Choose your own password to continue.",
  },
  newPasswordSubmit: {
    id: "access.loginStep.newPasswordSubmit",
    defaultMessage: "Set password and continue",
  },
});

export const navMessages = defineMessages({
  consoleUsers: { id: "access.nav.consoleUsers", defaultMessage: "Console users" },
  identityProvider: { id: "access.nav.identityProvider", defaultMessage: "Single sign-on" },
  audit: { id: "access.nav.audit", defaultMessage: "Audit log" },
});

export const roleMessages = defineMessages({
  viewer: { id: "access.role.viewer", defaultMessage: "Viewer" },
  analyst: { id: "access.role.analyst", defaultMessage: "Analyst" },
  administrator: { id: "access.role.administrator", defaultMessage: "Administrator" },
  none: { id: "access.role.none", defaultMessage: "Remove access" },
});

export const usersMessages = defineMessages({
  title: { id: "access.users.title", defaultMessage: "Console users" },
  noPermissionTitle: {
    id: "access.users.noPermission.title",
    defaultMessage: "You cannot manage console users",
  },
  noPermissionDescription: {
    id: "access.users.noPermission.description",
    defaultMessage: "Managing console users needs the Administrator role on an operator.",
  },
  operator: { id: "access.users.operator", defaultMessage: "Operator" },
  inviteUser: { id: "access.users.inviteUser", defaultMessage: "Invite user" },
  moreActions: { id: "access.users.moreActions", defaultMessage: "More" },
  pendingInvitationsHeading: {
    id: "access.users.invitations.pendingHeading",
    defaultMessage: "Pending invitations",
  },
  allInvitations: { id: "access.users.invitations.all", defaultMessage: "All invitations" },
  linksNote: {
    id: "access.users.linksNote",
    defaultMessage:
      "Invitation links are single use and valid for 72 hours. Share them only with the recipient; S-Trust never shows or emails passwords.",
  },
  emptyTitle: { id: "access.users.empty.title", defaultMessage: "No console users yet" },
  emptyDescription: {
    id: "access.users.empty.description",
    defaultMessage: "Invite a colleague to give them access to this operator.",
  },
  inviteHeading: { id: "access.users.invite.heading", defaultMessage: "Invite a colleague" },
  inviteEmail: { id: "access.users.invite.email", defaultMessage: "Colleague's email" },
  inviteRole: { id: "access.users.invite.role", defaultMessage: "Role" },
  inviteSubmit: { id: "access.users.invite.submit", defaultMessage: "Create invitation" },
  inviteEmailRequired: {
    id: "access.users.invite.emailRequired",
    defaultMessage: "Enter a valid email.",
  },
  tableCaption: { id: "access.users.table.caption", defaultMessage: "Console users" },
  columnEmail: { id: "access.users.column.email", defaultMessage: "Email" },
  columnStatus: { id: "access.users.column.status", defaultMessage: "Status" },
  columnRole: { id: "access.users.column.role", defaultMessage: "Role" },
  columnPrecise: { id: "access.users.column.precise", defaultMessage: "Precise location" },
  columnActions: { id: "access.users.column.actions", defaultMessage: "Actions" },
  statusActive: { id: "access.users.status.active", defaultMessage: "Active" },
  statusLocked: { id: "access.users.status.locked", defaultMessage: "Locked" },
  statusDeactivated: { id: "access.users.status.deactivated", defaultMessage: "Deactivated" },
  noRole: { id: "access.users.noRole", defaultMessage: "No role" },
  ownAccount: {
    id: "access.users.ownAccount",
    defaultMessage: "You cannot change your own role or deactivate your own account.",
  },
  preciseGranted: { id: "access.users.precise.granted", defaultMessage: "Granted" },
  preciseNotGranted: { id: "access.users.precise.notGranted", defaultMessage: "Not granted" },
  grantPrecise: {
    id: "access.users.action.grantPrecise",
    defaultMessage: "Grant precise location",
  },
  unlock: { id: "access.users.action.unlock", defaultMessage: "Unlock" },
  passwordSetup: {
    id: "access.users.action.passwordSetup",
    defaultMessage: "Issue password-setup invitation",
  },
  deactivate: { id: "access.users.action.deactivate", defaultMessage: "Deactivate" },
  cancel: { id: "access.users.action.cancel", defaultMessage: "Cancel" },
  roleDialogTitle: { id: "access.users.roleDialog.title", defaultMessage: "Change role" },
  roleDialogSave: { id: "access.users.roleDialog.save", defaultMessage: "Save role" },
  grantTitle: { id: "access.users.grant.title", defaultMessage: "Grant precise location" },
  grantConfirm: { id: "access.users.grant.confirm", defaultMessage: "Grant access" },
  deactivateTitle: {
    id: "access.users.deactivate.title",
    defaultMessage: "Deactivate console user",
  },
  deactivateConfirm: {
    id: "access.users.deactivate.confirm",
    defaultMessage: "Deactivate user",
  },
  invitationsCaption: {
    id: "access.users.invitations.caption",
    defaultMessage: "Invitations of the operator",
  },
  invitationsEmpty: {
    id: "access.users.invitations.empty",
    defaultMessage: "There are no invitations.",
  },
  columnKind: { id: "access.users.invitations.kind", defaultMessage: "Type" },
  columnExpires: { id: "access.users.invitations.expires", defaultMessage: "Valid until" },
  kindAccount: { id: "access.users.invitations.kindAccount", defaultMessage: "New account" },
  kindPasswordSetup: {
    id: "access.users.invitations.kindPasswordSetup",
    defaultMessage: "Password setup",
  },
  kindPlatformAdministrator: {
    id: "access.users.invitations.kindPlatformAdministrator",
    defaultMessage: "Platform Administrator",
  },
  kindTwoFactorRecovery: {
    id: "access.users.invitations.kindTwoFactorRecovery",
    defaultMessage: "Two-factor recovery",
  },
  invitationPending: { id: "access.users.invitations.pending", defaultMessage: "Pending" },
  invitationAccepted: { id: "access.users.invitations.accepted", defaultMessage: "Accepted" },
  invitationExpired: { id: "access.users.invitations.expired", defaultMessage: "Expired" },
  invitationRevoked: { id: "access.users.invitations.revoked", defaultMessage: "Revoked" },
  revoke: { id: "access.users.invitations.revoke", defaultMessage: "Revoke" },
  revokeTitle: { id: "access.users.revoke.title", defaultMessage: "Revoke invitation" },
  revokeConfirm: { id: "access.users.revoke.confirm", defaultMessage: "Revoke invitation" },
  unlocked: { id: "access.users.unlocked", defaultMessage: "The account was unlocked." },
});

export const idpMessages = defineMessages({
  title: { id: "access.idp.title", defaultMessage: "Single sign-on" },
  intro: {
    id: "access.idp.intro",
    defaultMessage:
      "Register your organization's identity provider (SAML 2.0 or OIDC), test it and activate it. While it is active your users sign in through it; only Administrators keep password login as break-glass access.",
  },
  meta: {
    id: "access.idp.meta",
    defaultMessage: "Users sign in through your organization's identity provider.",
  },
  stepsLabel: { id: "access.idp.steps.label", defaultMessage: "Single sign-on setup progress" },
  stepsDone: { id: "access.idp.steps.done", defaultMessage: "completed" },
  stepRegister: { id: "access.idp.steps.register", defaultMessage: "Register" },
  stepTest: { id: "access.idp.steps.test", defaultMessage: "Test" },
  stepActivate: { id: "access.idp.steps.activate", defaultMessage: "Activate" },
  stepSignIn: { id: "access.idp.steps.signIn", defaultMessage: "Users sign in with SSO" },
  configOidc: { id: "access.idp.config.oidc", defaultMessage: "OIDC configuration" },
  configSaml: { id: "access.idp.config.saml", defaultMessage: "SAML 2.0 configuration" },
  clientSecretStored: {
    id: "access.idp.clientSecret.stored",
    defaultMessage: "(stored, never shown again)",
  },
  loading: { id: "access.idp.loading", defaultMessage: "Loading identity provider settings" },
  loadFailed: {
    id: "access.idp.loadFailed",
    defaultMessage: "We could not load the identity provider settings",
  },
  retry: { id: "access.idp.retry", defaultMessage: "Try again" },
  noPermissionTitle: {
    id: "access.idp.noPermission.title",
    defaultMessage: "Identity provider settings are not available",
  },
  noPermissionDescription: {
    id: "access.idp.noPermission.description",
    defaultMessage: "Only Administrators of the organization can open these settings.",
  },
  readOnly: {
    id: "access.idp.readOnly",
    defaultMessage:
      "These settings are read-only. Changing them needs the Administrator role on every operator of your organization.",
  },
  none: {
    id: "access.idp.none",
    defaultMessage: "No identity provider is registered for your organization yet.",
  },
  current: { id: "access.idp.current", defaultMessage: "Registered identity provider" },
  statusDraft: { id: "access.idp.status.draft", defaultMessage: "Draft" },
  statusActive: { id: "access.idp.status.active", defaultMessage: "Active" },
  statusInactive: { id: "access.idp.status.inactive", defaultMessage: "Inactive" },
  kind: { id: "access.idp.kind", defaultMessage: "Protocol" },
  kindSaml: { id: "access.idp.kind.saml", defaultMessage: "SAML 2.0" },
  kindOidc: { id: "access.idp.kind.oidc", defaultMessage: "OIDC" },
  registrationTitle: {
    id: "access.idp.registration.title",
    defaultMessage: "Values to register in your identity provider",
  },
  registrationIntro: {
    id: "access.idp.registration.intro",
    defaultMessage:
      "Enter these values in your identity provider when you create the application for the Explorer.",
  },
  registrationSamlAcs: {
    id: "access.idp.registration.samlAcs",
    defaultMessage: "SAML reply URL (assertion consumer service)",
  },
  registrationSamlEntityId: {
    id: "access.idp.registration.samlEntityId",
    defaultMessage: "SAML service provider identifier (entity id)",
  },
  registrationOidcRedirect: {
    id: "access.idp.registration.oidcRedirect",
    defaultMessage: "OIDC redirect URI",
  },
  registrationUnavailable: {
    id: "access.idp.registration.unavailable",
    defaultMessage: "These values are not available in this environment.",
  },
  samlUrl: { id: "access.idp.samlUrl", defaultMessage: "SAML metadata URL" },
  oidcIssuer: { id: "access.idp.oidcIssuer", defaultMessage: "OIDC issuer" },
  urlHint: { id: "access.idp.urlHint", defaultMessage: "An https address." },
  clientId: { id: "access.idp.clientId", defaultMessage: "Client id" },
  clientSecret: { id: "access.idp.clientSecret", defaultMessage: "Client secret" },
  clientSecretKeep: {
    id: "access.idp.clientSecret.keep",
    defaultMessage:
      "A secret is saved and cannot be read again. Type a new one to replace it, or leave this empty to keep it.",
  },
  clientSecretNew: {
    id: "access.idp.clientSecret.new",
    defaultMessage: "The secret is write-only: it is never shown again after saving.",
  },
  clientSecretMasked: {
    id: "access.idp.clientSecret.masked",
    defaultMessage: "Client secret (masked)",
  },
  lastTest: { id: "access.idp.lastTest", defaultMessage: "Last test" },
  neverTested: { id: "access.idp.neverTested", defaultMessage: "Not tested since the last change" },
  urlInvalid: {
    id: "access.idp.urlInvalid",
    defaultMessage: "Enter an https address.",
  },
  clientIdRequired: { id: "access.idp.clientIdRequired", defaultMessage: "Enter the client id." },
  clientSecretRequired: {
    id: "access.idp.clientSecretRequired",
    defaultMessage: "Enter the client secret.",
  },
  save: { id: "access.idp.save", defaultMessage: "Save" },
  saved: {
    id: "access.idp.saved",
    defaultMessage: "Saved. Run a test before activating.",
  },
  test: { id: "access.idp.test", defaultMessage: "Test connection" },
  testSucceeded: {
    id: "access.idp.test.succeeded",
    defaultMessage: "The test succeeded. You can activate it.",
  },
  testFailedNotice: {
    id: "access.idp.test.failed",
    defaultMessage:
      "The test failed. Check the settings and the identity provider, then test again.",
  },
  activate: { id: "access.idp.activate", defaultMessage: "Activate" },
  activated: {
    id: "access.idp.activated",
    defaultMessage: "Activated. Your users now sign in through it.",
  },
  deactivate: { id: "access.idp.deactivate", defaultMessage: "Deactivate" },
  deactivated: {
    id: "access.idp.deactivated",
    defaultMessage: "Deactivated. Users sign in with email and password again.",
  },
  deactivateTitle: {
    id: "access.idp.deactivate.title",
    defaultMessage: "Deactivate single sign-on",
  },
  deactivateBody: {
    id: "access.idp.deactivate.body",
    defaultMessage:
      "Users will sign in with email and password again. Users who never had a password need a password-setup invitation, which you issue from the console users screen.",
  },
  deactivateConfirm: {
    id: "access.idp.deactivate.confirm",
    defaultMessage: "Deactivate single sign-on",
  },
  cancel: { id: "access.idp.cancel", defaultMessage: "Cancel" },
});

export const weakPasswordMinLengthMessage = defineMessage<{ minLength: number }>(
  {
    id: "access.error.weakPassword.minLength",
    defaultMessage: "The password must have at least {minLength} characters.",
  },
  { typed: true },
);

export const invitationAccountMessage = defineMessage<{ email: string }>(
  { id: "access.invitation.account", defaultMessage: "Account: {email}" },
  { typed: true },
);

export const invitationExpiresMessage = defineMessage<{ expiresAt: string }>(
  { id: "access.invitation.expires", defaultMessage: "Valid until {expiresAt}" },
  { typed: true },
);

export const usersChangeRoleForMessage = defineMessage<{ email: string }>(
  { id: "access.users.action.changeRoleFor", defaultMessage: "Change role of {email}" },
  { typed: true },
);

export const usersMetaMessage = defineMessage<{ operator: string }>(
  {
    id: "access.users.meta",
    defaultMessage: "{operator} \u00b7 invite colleagues, set roles and precise-location access.",
  },
  { typed: true },
);

export const usersMoreForMessage = defineMessage<{ email: string }>(
  { id: "access.users.action.moreFor", defaultMessage: "More actions for {email}" },
  { typed: true },
);

export const usersGrantPreciseForMessage = defineMessage<{ email: string }>(
  {
    id: "access.users.action.grantPreciseFor",
    defaultMessage: "Grant precise location to {email}",
  },
  { typed: true },
);

export const usersUnlockForMessage = defineMessage<{ email: string }>(
  { id: "access.users.action.unlockFor", defaultMessage: "Unlock {email}" },
  { typed: true },
);

export const usersPasswordSetupForMessage = defineMessage<{ email: string }>(
  {
    id: "access.users.action.passwordSetupFor",
    defaultMessage: "Issue password-setup invitation to {email}",
  },
  { typed: true },
);

export const usersDeactivateForMessage = defineMessage<{ email: string }>(
  { id: "access.users.action.deactivateFor", defaultMessage: "Deactivate {email}" },
  { typed: true },
);

export const usersRoleDialogLabelMessage = defineMessage<{ email: string }>(
  { id: "access.users.roleDialog.label", defaultMessage: "Role of {email}" },
  { typed: true },
);

export const usersGrantBodyMessage = defineMessage<{ email: string }>(
  {
    id: "access.users.grant.body",
    defaultMessage:
      "{email} will be able to view and export precise coordinates of this operator. Each read is recorded in the audit log.",
  },
  { typed: true },
);

export const usersDeactivateBodyMessage = defineMessage<{ email: string }>(
  {
    id: "access.users.deactivate.body",
    defaultMessage:
      "{email} will be signed out now and will not be able to sign in again. To bring them back, invite the same email again: the invitation reactivates the account with the role it grants.",
  },
  { typed: true },
);

export const usersRevokeForMessage = defineMessage<{ email: string }>(
  { id: "access.users.invitations.revokeFor", defaultMessage: "Revoke invitation for {email}" },
  { typed: true },
);

export const usersRevokeBodyMessage = defineMessage<{ email: string }>(
  {
    id: "access.users.revoke.body",
    defaultMessage:
      "The invitation for {email} will stop working. Nobody will be able to accept it.",
  },
  { typed: true },
);

export const usersLockedForMessage = defineMessage<{ minutes: number }>(
  { id: "access.users.lockedFor", defaultMessage: "Locked \u00b7 {minutes} min left" },
  { typed: true },
);

export const idpTestPassedMessage = defineMessage<{ testedAt: string }>(
  { id: "access.idp.testPassed", defaultMessage: "Passed on {testedAt}" },
  { typed: true },
);

export const idpTestFailedMessage = defineMessage<{ testedAt: string }>(
  { id: "access.idp.testFailed", defaultMessage: "Failed on {testedAt}" },
  { typed: true },
);

// Two-factor reset by an Administrator (change add-console-account-settings, design D7).
export const usersResetTwoFactorMessages = defineMessages({
  action: { id: "access.users.resetTwoFactor.action", defaultMessage: "Reset two-factor" },
  title: {
    id: "access.users.resetTwoFactor.title",
    defaultMessage: "Reset two-factor authentication?",
  },
  confirm: { id: "access.users.resetTwoFactor.confirm", defaultMessage: "Reset and create link" },
});
export const usersResetTwoFactorForMessage = defineMessage<{ email: string }>(
  {
    id: "access.users.resetTwoFactor.actionFor",
    defaultMessage: "Reset the two-factor authentication of {email}",
  },
  { typed: true },
);
export const usersResetTwoFactorBodyMessage = defineMessage<{ email: string }>(
  {
    id: "access.users.resetTwoFactor.body",
    defaultMessage:
      "{email} will be signed out, and their password sign-in will wait for a recovery link. You get a single-use link, valid for 72 hours, to hand over through a channel you trust; with it they set a new password and a new authenticator.",
  },
  { typed: true },
);
